<script setup lang="ts">
import { getPricePresentation } from "#shared/lib/price-presentation";
import ProductBreadCrumbs from "./product-bread-crumbs.vue";
import ProductMedia from "./product-media.vue";
import ProductInfo from "./product-info.vue";
import type { StoreProductCategory, StoreProductVariant } from "@medusajs/types";
import { createEcommerceProduct } from "#shared/lib/ecommerce-product";
import { buildProductBreadcrumbs } from "~/shared/lib/breadcrumbs";
import { normalizeProductWithDisplayTags } from "#shared/types/product-display-tag";
import { useEcommerceAnalytics } from "~/features/ecommerce-analytics";
import { useProductStore } from "../lib/product-store";
import { useRecentlyViewedStore } from "~/features/recently-viewed";
import { getProductByHandle } from "../api/get-product-by-handle";
import {
    hasMeaningfulQueryValue,
    INDEXABLE_ROBOTS,
    NOINDEX_FOLLOW_ROBOTS,
} from "#shared/lib/seo";
import { useBreadcrumbSchema } from "~/shared/lib/use-breadcrumb-schema";
import { resolveSeoMeta, truncateDescription } from "#shared/lib/seo-meta";
import { toAbsoluteSiteUrl } from "#shared/lib/site-url";
import { WidgetRecentlyViewed } from "~/widgets/recently-viewed";
import { WidgetProductBundles } from "~/widgets/product-bundles";
import type { MarketplaceAvailabilityResponse } from "../model/marketplace-availability";
import type { StoreProductBundlesResponse } from "#shared/types/product-bundle";
import { useClickAndCollectSalesChannelId } from "~/shared/lib/medusa";
import {
    getFirstAvailableProductVariant,
    getProductVariantPurchaseSource,
} from "~/shared/lib/product-availability";

const route = useRoute();
const client = useMedusaClient();
const searchClient = useSearchClient();
const clickAndCollectSalesChannelId = useClickAndCollectSalesChannelId();
const productStore = useProductStore();
const recentlyViewedStore = useRecentlyViewedStore();
const ecommerceAnalytics = useEcommerceAnalytics();
const { variant, price } = storeToRefs(productStore);
const siteConfig = useSiteConfig();
const canonicalPath = computed(
    () => `/products/${route.params.handle as string}`,
);
const canonicalUrl = computed(() =>
    toAbsoluteSiteUrl(siteConfig.url, canonicalPath.value),
);
const isIndexableProductPage = computed(
    () =>
        !Object.entries(route.query).some(([key, value]) => {
            if (key === "variant") return hasMeaningfulQueryValue(value);
            return hasMeaningfulQueryValue(value);
        }),
);

if (!route.params.handle || route.params.handle === "undefined")
    throw createError({
        message: "Товар не найден",
        statusCode: 404,
        fatal: true,
        data: {
            reason: "В адресе страницы отсутствует корректный handle товара",
            handle: route.params.handle,
        },
    });

const { data: product, error } = await useAsyncData(
    () => route.params.handle as string,
    () =>
        getProductByHandle({
            client,
            searchClient,
            handle: route.params.handle as string,
            salesChannelId: clickAndCollectSalesChannelId,
        }),
    {
        transform: (response) => {
            if (!response) return undefined;
            const [r, i] = response;
            const resolvedProduct = r.product;
            const inventoryVariants = i.products?.[0]?.variants ?? [];
            const extendedProduct = {
                ...resolvedProduct,
                variants:
                    resolvedProduct?.variants?.map((v) => ({
                        ...v,
                        inventory_quantity:
                            inventoryVariants.find((l) => l.id === v.id)
                                ?.inventory_quantity ?? 0,
                    })) ?? [],
            };
            return resolvedProduct
                ? {
                      ...normalizeProductWithDisplayTags(extendedProduct),
                      isInClickAndCollectChannel: Boolean(i.products?.length),
                  }
                : undefined;
        },
        watch: [() => route.params.handle as string],
    },
);
if (error.value)
    throw createError({
        message: error.value.statusCode === 404
            ? "Товар не найден"
            : "Не удалось загрузить товар. Попробуйте позже.",
        statusCode: error.value.statusCode === 404 ? 404 : 503,
        fatal: true,
        cause: error.value,
        data: { handle: route.params.handle },
    });

if (!product.value)
    throw createError({
        message: "Товар не найден",
        statusCode: 404,
        fatal: true,
        data: {
            reason: "Meilisearch не вернул id товара для указанного handle",
            handle: route.params.handle,
        },
    });

const { data: marketplaceAvailability } = await useAsyncData(
    () => `product-${product.value?.id}-marketplace-availability`,
    () =>
        client.client.fetch<MarketplaceAvailabilityResponse>(
            `/store/products/${product.value!.id}/marketplace-availability`,
        ),
    {
        watch: [() => product.value?.id],
    },
);

function isVariantAvailable(value?: StoreProductVariant | null) {
    return getProductVariantPurchaseSource(
        value,
        product.value?.isInClickAndCollectChannel ?? false,
        value ? marketplaceAvailability.value?.variants[value.id] : [],
    ) !== "offline";
}

const { data: productBundlesResponse } = await useAsyncData(
    () => `product-${product.value?.id}-bundles`,
    () =>
        client.client.fetch<StoreProductBundlesResponse>(
            "/store/product-bundles",
            {
                query: {
                    product_id: product.value!.id,
                },
            },
        ),
    {
        watch: [() => product.value?.id],
        default: () => ({ product_bundles: [] }),
    },
);

const { data: product_categories } =
    useNuxtData<StoreProductCategory[]>("categories");

const productMeta = computed(() =>
    resolveSeoMeta({
        canonical: canonicalUrl.value,
        title: product.value?.title
            ? `${product.value.title} - купить в ${siteConfig.name}`
            : siteConfig.name,
        description: truncateDescription(
            product.value?.title
                ? `Купить ${product.value.title} в интернет-магазине ${siteConfig.name}. Актуальные цены, описание, характеристики и удобная доставка.`
                : product.value?.description,
        ),
        image: product.value?.images?.[0]?.url,
        robots: isIndexableProductPage.value
            ? INDEXABLE_ROBOTS
            : NOINDEX_FOLLOW_ROBOTS,
    }),
);

const productTitle = computed(
    () =>
        (variant.value?.metadata?.name as string | undefined) ??
        product.value?.title,
);

const sku = computed(() => {
    if (variant.value?.sku?.includes("-")) {
        return variant.value.sku.split("-").slice(2, -1).join("-");
    }

    return variant.value?.sku;
});

const schemaVariant = computed(() => {
    const queryVariant = Array.isArray(route.query.variant)
        ? route.query.variant[0]
        : route.query.variant;

    const requestedVariant = product.value?.variants?.find(
        (item) => item.id === queryVariant,
    );

    return isVariantAvailable(requestedVariant)
        ? requestedVariant
        : getFirstAvailableProductVariant(product.value?.variants, isVariantAvailable) ??
              product.value?.variants?.[0];
});
const schemaProductTitle = computed(
    () =>
        (schemaVariant.value?.metadata?.name as string | undefined) ??
        product.value?.title,
);
const schemaSku = computed(() => {
    const value = schemaVariant.value?.sku;
    return value?.includes("-")
        ? value.split("-").slice(2, -1).join("-")
        : value;
});
const schemaPrice = computed(
    () => schemaVariant.value?.calculated_price?.calculated_amount,
);
const schemaPriceCurrency = computed(
    () =>
        schemaVariant.value?.calculated_price?.currency_code?.toUpperCase() ??
        "RUB",
);
const schemaProductImage = computed(
    () => product.value?.images?.[0]?.url ?? product.value?.thumbnail,
);
const schemaProductPriceLabel = computed(() =>
    typeof schemaPrice.value === "number"
        ? `${schemaPrice.value.toLocaleString("ru-RU")} ${
              schemaPriceCurrency.value === "RUB"
                  ? "₽"
                  : schemaPriceCurrency.value
          }`
        : undefined,
);
const schemaAvailability = computed(() => {
    const value = schemaVariant.value;
    if (!value) return "https://schema.org/OutOfStock";

    return isVariantAvailable(value)
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock";
});

if (import.meta.server) {
    defineOgImage(
        "Product",
        {
            brand: siteConfig.name,
            image: schemaProductImage,
            price: schemaProductPriceLabel,
            sku: schemaSku,
            title: schemaProductTitle,
        },
        {
            alt: schemaProductTitle,
            fonts: [
                {
                    name: "Euclid Circular B",
                    path: "/fonts/euclid-ttf/Euclid Circular B Regular.ttf",
                    weight: 400,
                },
                {
                    name: "Euclid Circular B",
                    path: "/fonts/euclid-ttf/Euclid Circular B SemiBold.ttf",
                    weight: 600,
                },
            ],
            height: 630,
            width: 1200,
        },
    );
}

const breadcrumbItems = computed(() => {
    if (!product.value) return [];

    return buildProductBreadcrumbs(product_categories.value, {
        categories: product.value.categories,
        handle: product.value.handle,
        title: productTitle.value || "Товар",
    }).map((item) => ({
        item: toAbsoluteSiteUrl(siteConfig.url, item.path),
        name: item.label,
    }));
});

useHead(() => ({
    link: [
        {
            rel: "canonical",
            href: productMeta.value.canonical,
        },
    ],
}));

useSeoMeta({
    title: () => productMeta.value.title,
    description: () => productMeta.value.description,
    robots: () => productMeta.value.robots,
    ogTitle: () => productMeta.value.ogTitle,
    ogDescription: () => productMeta.value.ogDescription,
    ogUrl: () => productMeta.value.ogUrl,
    ogType: "product",
    ogImage: () => productMeta.value.ogImage,
    twitterCard: "summary_large_image",
    twitterTitle: () => productMeta.value.ogTitle,
    twitterDescription: () => productMeta.value.ogDescription,
    twitterImage: () => productMeta.value.ogImage,
});

useBreadcrumbSchema(breadcrumbItems);

useSchemaOrg([
    defineProduct({
        name: () => schemaProductTitle.value,
        description: () => product.value?.description,
        image: () =>
            product.value?.images
                ?.map((image) => image.url)
                .filter((image): image is string => Boolean(image)) ?? [],
        sku: () => schemaSku.value,
        brand: () => ({
            "@type": "Brand",
            name: siteConfig.name,
        }),
        url: () => productMeta.value.canonical,
        offers: () =>
            typeof schemaPrice.value === "number"
                ? {
                      "@type": "Offer",
                      price: schemaPrice.value,
                      priceCurrency: schemaPriceCurrency.value,
                      availability: schemaAvailability.value,
                      url: productMeta.value.canonical,
                  }
                : undefined,
    }),
]);

productStore.setProduct(product.value);

onMounted(() => {
    if (!product.value) return;
    recentlyViewedStore.addProduct(product.value);
});

watch(
    [() => route.query?.variant, () => product.value?.id, marketplaceAvailability],
    () => {
        if (route.query.variant && product.value?.variants) {
            const id = route.query.variant as string;
            const requestedVariant = product.value.variants.find(
                (item) => item.id === id,
            );
            const nextVariant = isVariantAvailable(requestedVariant)
                ? requestedVariant
                : getFirstAvailableProductVariant(product.value.variants, isVariantAvailable) ??
                  requestedVariant ??
                  product.value.variants[0];

            if (nextVariant) productStore.setVariant(nextVariant);
        } else {
            const nextVariant = getFirstAvailableProductVariant(
                product.value?.variants,
                isVariantAvailable,
            ) ?? product.value?.variants?.[0];
            if (nextVariant) productStore.setVariant(nextVariant);
        }
    },
    {
        deep: true,
        immediate: true,
    },
);

watch(
    [() => product.value?.id, () => variant.value?.id],
    () => {
        if (!import.meta.client || !product.value || !variant.value) return;

        ecommerceAnalytics.track({
            type: "view_item",
            currency: "RUB",
            products: [
                createEcommerceProduct(product.value, {
                    brand: siteConfig.name,
                    variant: variant.value,
                }),
            ],
        });
    },
    { immediate: true },
);

watch(
    variant,
    async (value) => {
        if (!value) return;
        const { price, compare_at_price, discount, code } =
            await client.client.fetch<{
                price: number;
                compare_at_price: number;
                discount: number;
                code: number;
            }>(`/store/variants/${value.id}/price`);
        if (variant.value?.id !== value.id) return;
        const presentation = getPricePresentation({ amount: price, compareTo: compare_at_price, code, discount });
        productStore.setPrice(price);
        productStore.priceCode = code;
        productStore.setOldPrice(presentation.oldPrice);
        productStore.setDiscount(presentation.discount);
    },
    {
        deep: true,
        immediate: true,
    },
);
</script>
<template>
    <div v-if="product" class="mt-12 mb-12 lg:mt-24">
        <div class="container px-4 mx-auto">
            <ProductBreadCrumbs :product="product" />
            <div
                class="grid mb-9 lg:mb-12 gap-4 md:gap-8 grid-cols-1 lg:grid-cols-2"
            >
                <ProductMedia class="" :product="product" />
                <ProductInfo
                    class="lg:max-w-106"
                    :is-in-click-and-collect-channel="product.isInClickAndCollectChannel"
                    :marketplace-availability="
                        marketplaceAvailability?.variants
                    "
                />
            </div>
            <WidgetProductBundles
                :current-product-id="product.id"
                :bundles="productBundlesResponse.product_bundles"
            />
            <ClientOnly>
                <WidgetRecentlyViewed :current-product-id="product.id" />
            </ClientOnly>
        </div>
    </div>
</template>
